import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { data, type Api } from "../api/client";
import { useT } from "../i18n";
import { QueryView } from "../ui/states";
import { useBff, useCurrentTenant, useMe } from "./session";

export function Page({ title, description, actions, children }: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          {description && <p className="text-[var(--color-muted)]">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
      </header>
      {children}
    </div>
  );
}

/** A tenant-scoped API read, cached under its key (a tenant switch drops it). `refetchInterval` may
 * look at the latest data: poll while something is still changing. */
export function useApiQuery<T>(
  key: string,
  load: (api: Api) => Promise<{ data?: T; response: Response }>,
  options: {
    enabled?: boolean;
    refetchInterval?: number | ((latest: T | undefined) => number | false);
    /** While another key loads, the last answer stays shown (no loading state in between). */
    keepPrevious?: boolean;
  } = {},
) {
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const every = options.refetchInterval;
  return useQuery({
    queryKey: [key, tenant], queryFn: () => data(load(api)), enabled: options.enabled,
    refetchInterval: typeof every === "function" ? (query) => every(query.state.data) : every,
    placeholderData: options.keepPrevious ? keepPreviousData : undefined,
  });
}

/** What a permission-denied state says: the user's role, in which tenant. */
export function useDenied() {
  const t = useT();
  const me = useMe();
  const tenant = useCurrentTenant();
  return { role: me.role ? t(`role.${me.role}`) : "—", tenant: tenant?.name ?? "—" };
}

export function Section<T>({ title, query, children }: {
  title?: string;
  query: ReturnType<typeof useApiQuery<T>>;
  children: (data: T) => ReactNode;
}) {
  const denied = useDenied();
  return (
    <section className="space-y-3">
      {title && <h2 className="text-lg font-semibold">{title}</h2>}
      <QueryView query={query} denied={denied}>
        {children}
      </QueryView>
    </section>
  );
}
